import React, { useContext, useState } from 'react';
import { Badge, Button, Card, Modal, Dropdown, Form } from 'react-bootstrap';
import axios from 'axios';
import ProjectContext from '../context/ProjectContext';
import AuthContext from '../context/AuthContext';
import { formatCurrency, cleanPayee } from '../utils/formatters';

// Components
import TransactionRow from '../components/transactions/TransactionRow';
import ImportSection from '../components/transactions/ImportSection';
import FilterSection from '../components/transactions/FilterSection';
import PaginationSection from '../components/transactions/PaginationSection';
import ImportSummaryModal from '../components/transactions/ImportSummaryModal';
import DataTable from '../components/common/DataTable';
import SearchableSelect from '../components/common/SearchableSelect';
import AddTransactionModal from '../components/transactions/AddTransactionModal';
import JournalEntryModal from '../components/transactions/JournalEntryModal';
import AICategorizationModal from '../components/transactions/AICategorizationModal';
import AdvancedRuleModal from '../components/rules/AdvancedRuleModal';
import { BulkActionBanner } from '../components/transactions/BulkActionBanner';
import { SimilarTransactionsModal } from '../components/transactions/SimilarTransactionsModal';

// Hooks
import { useTransactions } from '../hooks/useTransactions';
import { useImport } from '../hooks/useImport';
import { useProjectAccounts } from '../hooks/useProjectAccounts';
import usePayees from '../hooks/usePayees';
import useClasses from '../hooks/useClasses';
import { useRuleForm } from '../hooks/useRuleForm';

// Styles
import './Transactions.css';

const Transactions = () => {
  const { activeProject, updateProject } = useContext(ProjectContext);
  const { user } = useContext(AuthContext);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showJournalModal, setShowJournalModal] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [editingJournalId, setEditingJournalId] = useState(null);
  const [syncingAccountId, setSyncingAccountId] = useState(null);
  
  // Bulk post modal states
  const [showBulkPostModal, setShowBulkPostModal] = useState(false);
  const [originalTx, setOriginalTx] = useState(null);
  const [similarTxs, setSimilarTxs] = useState([]);
  const [selectedSimilarIds, setSelectedSimilarIds] = useState([]);
  
  const handleSync = async (accountId) => {
    if (!accountId) return;
    setSyncingAccountId(accountId);
    try {
      const res = await axios.post(`/api/projects/${activeProject._id}/plaid/sync/${accountId}`);
      alert(`Sync successful! Added ${res.data.addedCount || 0} new transactions.`);
      fetchTransactions();
      fetchAccounts();
    } catch (err) {
      console.error('Sync error:', err);
      alert('Failed to sync with bank: ' + (err.response?.data?.msg || ''));
    } finally {
      setSyncingAccountId(null);
    }
  };

  const projectMember = activeProject?.members?.find(m => m.user === user?._id);
  const isEditor = user?.type === 'admin' || projectMember?.role === 'editor';

  const handleEditJournal = (journalId) => {
    setEditingJournalId(journalId);
    setShowJournalModal(true);
  };

  const plTypes = ['Income', 'Other Income', 'Cost of Goods Sold', 'Expenses', 'Expense', 'Other Expense'];
  const bsTypes = ['Bank', 'Credit Card', 'Accounts receivable (A/R)', 'Accounts payable (A/P)', 'Other Current Assets', 'Other Current Liabilities', 'Fixed Assets', 'Long Term Liabilities', 'Equity'];

  // Data Hooks
  const { accounts, fetchAccounts, addAccount } = useProjectAccounts(activeProject);
  const { payees, fetchPayees, addPayee } = usePayees();
  const { classes, fetchClasses, addClass } = useClasses();

  React.useEffect(() => {
    if (activeProject) {
      fetchPayees(activeProject._id, { all: 'true' });
      fetchClasses(activeProject._id);
    }
  }, [activeProject, fetchPayees, fetchClasses]);
  
  const {
    transactions, loading: txLoading, updatingTxs, error,
    page, setPage, limit, setLimit, total, pages, 
    startDate, setStartDate, endDate, setEndDate, 
    accountIdFilter, setAccountIdFilter,
    categoryFilter, setCategoryFilter,
    payeeFilter, setPayeeFilter,
    classFilter, setClassFilter,
    descriptionFilter, setDescriptionFilter,
    onlyUncategorized, setOnlyUncategorized,
    postingFilter, setPostingFilter,
    sortBy, sortOrder, 
    handleSort, fetchTransactions, updateTransaction,
    deleteTransaction,
    bulkCategorize
  } = useTransactions(activeProject, true, fetchAccounts);

  const handleUpdateTransaction = async (txId, updates) => {
    if (updates.posting === true) {
      const tx = transactions.find(t => t._id === txId);
      if (tx) {
        // Validate category mapping before any further action
        const hasCategory = tx.category && tx.category !== 'Uncategorized';
        if (!tx.fromAccount || !tx.toAccount || !hasCategory) {
          alert('Cannot post uncategorized transaction. Please assign a Category/Transfer account first.');
          return;
        }

        try {
          const response = await axios.get(`/api/projects/${activeProject._id}/transactions/similar-pending?description=${encodeURIComponent(tx.description)}&excludeId=${tx._id}`);
          if (response.data && response.data.length > 0) {
            setOriginalTx(tx);
            setSimilarTxs(response.data);
            setSelectedSimilarIds(response.data.map(t => t._id));
            setShowBulkPostModal(true);
            return;
          }
        } catch (err) {
          console.error('Error checking for similar transactions:', err);
        }
      }
    }
    await updateTransaction(txId, updates, accounts);
  };

  const applyRule = async (ruleId) => {
    try {
      await axios.post(`/api/projects/${activeProject._id}/rules/${ruleId}/apply`);
      fetchTransactions(true);
      fetchAccounts();
    } catch (err) {
      console.error('Error applying rule:', err);
    }
  };

  const {
    showRuleModal, setShowRuleModal,
    editingRuleId,
    ruleFormData, setRuleFormData,
    previewTxs,
    handleSaveAdvancedRule,
    handleUpdateRule,
    addCondition,
    removeCondition,
    updateCondition
  } = useRuleForm(activeProject, accounts, plTypes, applyRule, () => {}, () => {}, fetchPayees);

  const handleOpenRuleCreator = (tx) => {
    const cleanDescription = cleanPayee(tx.description);
    setRuleFormData({
      name: `Rule for ${cleanDescription}`,
      transactionType: tx.amount >= 0 ? 'Money In' : 'Money Out',
      logicalOperator: 'AND',
      conditions: [
        { field: 'description', operator: 'contains', value: cleanDescription }
      ],
      targetAccountId: tx.toAccount?._id || tx.toAccount || '',
      targetPayeeId: tx.payeeId?._id || tx.payeeId || '',
      targetClassId: tx.classId?._id || tx.classId || '',
      newCategoryName: '',
      newCategoryType: tx.amount >= 0 ? 'Income' : 'Expenses',
      autoAdd: false
    });
    setShowRuleModal(true);
  };

  React.useEffect(() => {
    if (accounts && accounts.length > 0 && !accountIdFilter) {
      const defaultAccount = accounts.find(a => ['Bank', 'Credit Card'].includes(a.type));
      if (defaultAccount) {
        setAccountIdFilter(defaultAccount._id);
      }
    }
  }, [accounts, accountIdFilter, setAccountIdFilter]);

  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkPayee, setBulkPayee] = useState('');
  const [bulkClass, setBulkClass] = useState('');

  const toggleSelect = (txId) => {
    if (selectedIds.includes(txId)) {
        setSelectedIds(selectedIds.filter(id => id !== txId));
    } else {
        setSelectedIds([...selectedIds, txId]);
    }
  };

  const handleBulkCategorize = async () => {
    if (!bulkCategory && !bulkPayee && !bulkClass) return alert('Please select a category, payee, or class to apply');
    
    let targetAccId = undefined;
    let targetCategory = undefined;

    if (bulkCategory) {
      if (bulkCategory === 'Uncategorized') {
        targetCategory = 'Uncategorized';
        targetAccId = null;
      } else {
        const targetAcc = accounts.find(a => a.name === bulkCategory || a._id === bulkCategory);
        if (targetAcc) {
          targetAccId = targetAcc._id;
          targetCategory = targetAcc.name;
        }
      }
    }
    
    const success = await bulkCategorize(
      selectedIds, 
      targetAccId, 
      targetCategory, 
      bulkPayee || undefined,
      bulkClass || undefined
    );

    if (success) {
        setSelectedIds([]);
        setBulkCategory('');
        setBulkPayee('');
        setBulkClass('');
    } else {
        alert('Failed to update transactions.');
    }
  };

  const handleBulkAction = async (actionType) => {
    let confirmMsg = '';
    let postingFlag = undefined;
    let excludedFlag = undefined;

    if (actionType === 'post') {
      // Client-side validation: prevent bulk posting if any transaction is uncategorized
      for (const txId of selectedIds) {
        const tx = transactions.find(t => t._id === txId);
        if (tx) {
          const hasCategory = tx.category && tx.category !== 'Uncategorized';
          if (!tx.fromAccount || !tx.toAccount || !hasCategory) {
            alert(`Cannot post. The transaction "${tx.description}" is uncategorized. Please assign a Category/Transfer account first.`);
            return;
          }
        }
      }
      postingFlag = true;
    } else if (actionType === 'unpost') {
      confirmMsg = `Are you sure you want to unpost ${selectedIds.length} transaction(s) and move them back to Pending?`;
      postingFlag = false;
    } else if (actionType === 'exclude') {
      confirmMsg = `Are you sure you want to exclude ${selectedIds.length} transaction(s)?`;
      excludedFlag = true;
    } else if (actionType === 'restore') {
      confirmMsg = `Are you sure you want to restore ${selectedIds.length} transaction(s) to Pending?`;
      excludedFlag = false;
    }

    if (confirmMsg && !window.confirm(confirmMsg)) return;

    // Trigger update via bulkCategorize (without changing category/payee/class)
    const success = await bulkCategorize(
      selectedIds,
      undefined,
      undefined,
      undefined,
      undefined,
      postingFlag,
      excludedFlag
    );

    if (success) {
      setSelectedIds([]);
      fetchAccounts();
    } else {
      alert(`Failed to perform bulk ${actionType} action.`);
    }
  };

  const {
    file, setFile, selectedAccount, setSelectedAccount,
    dragging, importSummary, showSummary, setShowSummary,
    tempData, setTempData, loading: importLoading,
    onImport, onFinalize, handleDrag, handleDrop
  } = useImport(activeProject, isEditor, fetchTransactions, fetchAccounts);

  const columnConfig = activeProject?.columnConfig || {
    showPayee: true,
    showClass: true,
    showNote: true
  };

  const tableHeaders = [
    { label: '', width: '30px' },
    { label: 'Date', key: 'date', sortable: true, width: '100px' },
    { label: 'Description', key: 'description', sortable: true },
    { label: 'Amount', key: 'amount', sortable: true, align: 'right', width: '120px' }
  ];

  if (columnConfig.showPayee || columnConfig.showClass) {
    let colLabel = '';
    if (columnConfig.showPayee && columnConfig.showClass) {
      colLabel = <div>Payee<br />Class</div>;
    } else if (columnConfig.showPayee) {
      colLabel = 'Payee';
    } else {
      colLabel = 'Class';
    }
    tableHeaders.push({ label: colLabel, width: '160px' });
  }

  tableHeaders.push({ label: <div>Category<br />Transfer</div>, width: '140px' });

  if (columnConfig.showNote) {
    tableHeaders.push({ label: 'Note', width: '150px' });
  }

  tableHeaders.push({ label: '', width: '120px', align: 'center' });

  if (!activeProject) return <div className="p-5 text-center">Please select a company first.</div>;

  return (
    <div className="transactions-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Transactions</h1>
        <div>
          {isEditor && (
            <>
              {/* <Button variant="outline-info" size="sm" onClick={() => setShowAiModal(true)} className="mr-2">
                <i className="fas fa-magic mr-1"></i> AI Auto-Categorize
              </Button> */}
              <Button variant="outline-primary" size="sm" onClick={() => { setEditingJournalId(null); setShowJournalModal(true); }} className="mr-2">
                <i className="fas fa-book mr-1"></i> New Journal Entry
              </Button>
              <Button variant="primary" size="sm" onClick={() => setShowAddModal(true)} className="mr-2">
                <i className="fas fa-plus mr-1"></i> Add Manual Transaction
              </Button>
            </>
          )}
          {activeProject && (
            <Dropdown className="d-inline-block">
              <Dropdown.Toggle variant="outline-secondary" size="sm" id="columns-dropdown">
                <i className="fas fa-cog mr-1"></i> Columns
              </Dropdown.Toggle>
              <Dropdown.Menu className="p-3" style={{ minWidth: '180px' }}>
                <h6 className="dropdown-header px-0 mb-2">Toggle Columns</h6>
                <Form.Check 
                  type="checkbox"
                  id="toggle-payee"
                  label="Show Payee"
                  checked={columnConfig.showPayee}
                  onChange={async () => {
                    const newConfig = { ...columnConfig, showPayee: !columnConfig.showPayee };
                    await updateProject(activeProject._id, { columnConfig: newConfig });
                  }}
                  className="mb-2"
                />
                <Form.Check 
                  type="checkbox"
                  id="toggle-class"
                  label="Show Class"
                  checked={columnConfig.showClass}
                  onChange={async () => {
                    const newConfig = { ...columnConfig, showClass: !columnConfig.showClass };
                    await updateProject(activeProject._id, { columnConfig: newConfig });
                  }}
                  className="mb-2"
                />
                <Form.Check 
                  type="checkbox"
                  id="toggle-note"
                  label="Show Note"
                  checked={columnConfig.showNote}
                  onChange={async () => {
                    const newConfig = { ...columnConfig, showNote: !columnConfig.showNote };
                    await updateProject(activeProject._id, { columnConfig: newConfig });
                  }}
                />
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>
      </div>
      
      {error && <div className="alert alert-danger">{error}</div>}

      {/* Horizontal QuickBooks-style Bank/Credit Account cards */}
      <div className="d-flex overflow-auto mb-4 pb-2" style={{ gap: '16px', scrollbarWidth: 'thin' }}>
        {accounts.filter(a => ['Bank', 'Credit Card'].includes(a.type) && a.isActive !== false).map(acc => {
          const isSelected = accountIdFilter === acc._id;
          return (
            <Card 
              key={acc._id}
              className={`flex-shrink-0 border-0 ${isSelected ? 'account-card-selected shadow' : 'bg-light'}`}
              style={{ width: '220px', cursor: 'pointer', transition: 'all 0.2s' }}
              onClick={() => {
                setAccountIdFilter(isSelected ? '' : acc._id);
                setPage(1);
              }}
            >
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-start mb-1">
                  <h6 className={`small text-uppercase font-weight-bold ${isSelected ? 'text-white' : 'text-muted'} text-truncate mb-0`} style={{ maxWidth: '120px' }} title={acc.name}>
                    {acc.name}
                  </h6>
                  <div className="d-flex align-items-center">
                    {acc.plaidConnectionId && isEditor && (
                      <Button
                        variant="link"
                        className={`p-0 border-0 mr-1 ${isSelected ? 'text-white-50 text-white' : 'text-muted text-primary'}`}
                        style={{ fontSize: '0.8rem', lineHeight: 1 }}
                        disabled={syncingAccountId === acc._id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSync(acc._id);
                        }}
                      >
                        <i className={`fas fa-sync-alt ${syncingAccountId === acc._id ? 'fa-spin' : ''}`}></i>
                      </Button>
                    )}
                    <Badge variant={acc.type === 'Bank' ? 'info' : 'warning'} className="small px-1 py-0" style={{ fontSize: '0.65rem' }}>
                      {acc.type === 'Bank' ? 'Bank' : 'Card'}
                    </Badge>
                  </div>
                </div>
                <h4 className="mb-0 font-weight-bold">
                  {formatCurrency(acc.balance)}
                </h4>
              </Card.Body>
            </Card>
          );
        })}
      </div>
      
      <FilterSection 
        startDate={startDate} setStartDate={setStartDate}
        endDate={endDate} setEndDate={setEndDate}
        categoryFilter={categoryFilter} setCategoryFilter={setCategoryFilter}
        payeeFilter={payeeFilter} setPayeeFilter={setPayeeFilter}
        classFilter={classFilter} setClassFilter={setClassFilter}
        descriptionFilter={descriptionFilter} setDescriptionFilter={setDescriptionFilter}
        onlyUncategorized={onlyUncategorized} setOnlyUncategorized={setOnlyUncategorized}
        postingFilter={postingFilter} setPostingFilter={setPostingFilter}
        limit={limit} setLimit={setLimit}
        accounts={accounts}
        payees={payees}
        classes={classes}
        setPage={setPage}
        transactions={transactions}
        total={total}
        pages={pages}
        fetchTransactions={fetchTransactions}
      />

      {/* Bulk Action UI */}
      <BulkActionBanner
        selectedIds={selectedIds}
        accounts={accounts}
        plTypes={plTypes}
        bsTypes={bsTypes}
        payees={payees}
        classes={classes}
        bulkCategory={bulkCategory}
        setBulkCategory={setBulkCategory}
        bulkPayee={bulkPayee}
        setBulkPayee={setBulkPayee}
        bulkClass={bulkClass}
        setBulkClass={setBulkClass}
        handleBulkCategorize={handleBulkCategorize}
        postingFilter={postingFilter}
        handleBulkAction={handleBulkAction}
      />

      <DataTable 
        headers={tableHeaders}
        data={transactions}
        loading={txLoading && !showSummary}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        emptyMessage="No transactions found matching your filters."
        renderRow={(tx) => (
          <TransactionRow 
            key={tx._id} 
            tx={tx} 
            isSelected={selectedIds.includes(tx._id)}
            onSelect={toggleSelect}
            accounts={accounts} 
            payees={payees}
            classes={classes}
            isEditor={isEditor} 
            onUpdate={handleUpdateTransaction}
            onDelete={deleteTransaction}
            plTypes={plTypes}
            bsTypes={bsTypes}
            onEditJournal={handleEditJournal}
            isUpdating={!!updatingTxs[tx._id]}
            onCreateRule={(transaction) => {
              handleOpenRuleCreator(transaction);
            }}
            onAddPayee={async (name) => {
              const res = await addPayee(activeProject._id, name);
              if (res.success) {
                return res.data;
              } else {
                alert(res.error || 'Failed to add payee');
              }
            }}
            onAddClass={async (name) => {
              const res = await addClass(activeProject._id, name);
              if (res.success) {
                return res.data;
              } else {
                alert(res.error || 'Failed to add class');
              }
            }}
            onAddCategory={async (name, type) => {
              const res = await addAccount({ name, type });
              if (res.success) {
                return res.data;
              } else {
                alert(res.error || 'Failed to add category account');
              }
            }}
            columnConfig={columnConfig}
            selectedAccountId={accountIdFilter}
          />
        )}
      />

      <PaginationSection page={page} pages={pages} setPage={setPage} />

      <ImportSummaryModal 
        show={showSummary}
        onHide={() => { setShowSummary(false); setTempData(null); }}
        importSummary={importSummary}
        tempData={tempData}
        onFinalize={onFinalize}
        loading={importLoading}
      />

      <AddTransactionModal 
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        projectId={activeProject._id}
        accounts={accounts}
        payees={payees}
        classes={classes}
        fetchTransactions={fetchTransactions}
        plTypes={plTypes}
        bsTypes={bsTypes}
      />

      <JournalEntryModal 
        show={showJournalModal}
        onHide={() => { setShowJournalModal(false); setEditingJournalId(null); }}
        activeProject={activeProject}
        accounts={accounts}
        fetchTransactions={fetchTransactions}
        editingId={editingJournalId}
      />

      {/* AI Categorization Review Modal */}
      <AICategorizationModal 
        show={showAiModal}
        onHide={() => setShowAiModal(false)}
        activeProject={activeProject}
        accounts={accounts}
        fetchTransactions={fetchTransactions}
      />

      <AdvancedRuleModal
        show={showRuleModal}
        onHide={() => setShowRuleModal(false)}
        editingRuleId={editingRuleId}
        formData={ruleFormData}
        setFormData={setRuleFormData}
        onSave={editingRuleId ? handleUpdateRule : handleSaveAdvancedRule}
        accounts={accounts}
        payees={payees}
        classes={classes}
        plTypes={plTypes}
        bsTypes={bsTypes}
        previewTxs={previewTxs}
        addCondition={addCondition}
        removeCondition={removeCondition}
        updateCondition={updateCondition}
      />

      {/* Bulk Post Modal Confirmation */}
      <SimilarTransactionsModal
        show={showBulkPostModal}
        onHide={() => setShowBulkPostModal(false)}
        originalTx={originalTx}
        similarTxs={similarTxs}
        selectedSimilarIds={selectedSimilarIds}
        setSelectedSimilarIds={setSelectedSimilarIds}
        formatCurrency={formatCurrency}
        onPostJustThisOne={async () => {
          setShowBulkPostModal(false);
          await updateTransaction(originalTx._id, { posting: true }, accounts);
        }}
        onPostAllSelected={async () => {
          setShowBulkPostModal(false);
          await updateTransaction(originalTx._id, { posting: true }, accounts);
          try {
            await axios.post(`/api/projects/${activeProject._id}/transactions/bulk-post`, {
              transactionIds: selectedSimilarIds,
              updates: {
                toAccountId: originalTx.toAccount?._id || originalTx.toAccount,
                payeeId: originalTx.payeeId?._id || originalTx.payeeId,
                classId: originalTx.classId?._id || originalTx.classId,
                category: originalTx.category
              }
            });
            fetchTransactions();
            fetchAccounts();
          } catch (err) {
            console.error('Error bulk posting:', err);
            alert('Failed to bulk post transactions.');
          }
        }}
      />
    </div>
  );
};

export default Transactions;
